import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { getArticles } from "../../services/articleService";
import ArticleSearch from "../../components/Articles/ArticleSearch";
import ArticlesHero from "../../components/Articles/ArticlesHero";
import ArticleCategories from "../../components/Articles/ArticleCategories";
import FeaturedArticle from "../../components/Articles/FeaturedArticle";
import ArticleGrid from "../../components/Articles/ArticleGrid";
import { getArticleCategories } from "../../services/articleCategoryService";
import HomeContainer from "../../components/HomeContainer/HomeContainer";
import ArticlePagination from "./ArticlePagination/ArticlePagination";
import ArticleEmpty from "./ArticleEmpty/ArticleEmpty";
import "./Articles.css";


export default function Articles() {

    const [searchParams, setSearchParams] = useSearchParams();

    const [articles, setArticles] = useState([]);

    const [categories, setCategories] = useState([]);

    const [loading, setLoading] = useState(true);

    const [error, setError] = useState(null);

    const [pagination, setPagination] = useState(null);

    // const [page, setPage] = useState(Number(searchParams.get("page") || 1));

    /*
    |--------------------------------------------------------------------------
    | URL filters
    |--------------------------------------------------------------------------
    */

    const page =
        Number(searchParams.get("page")) || 1;

    const categoryId =
        searchParams.get("category") || "";

    const search =
        searchParams.get("search") || "";

    const sort =
        searchParams.get("sort") || "latest";


    /*
    |--------------------------------------------------------------------------
    | Load articles
    |--------------------------------------------------------------------------
    */

    useEffect(() => {

        let cancelled = false;

        async function loadArticles() {

            try {

                setLoading(true);

                setError(null);

                const response = await getArticles({
                    page: page,
                    perPage: 13,

                    categoryId:
                        categoryId || undefined,

                    search:
                        search || undefined,

                    sort:
                        sort || undefined,
                });

                if (cancelled) {
                    return;
                }

                setArticles(response.data || []);

                setPagination(response.meta || null);

            } catch (err) {

                if (cancelled) {
                    return;
                }

                console.error(
                    "Failed to load articles:",
                    err
                );

                setError(
                    err?.response?.data?.message ||
                    "خطا در دریافت مقالات"
                );

                setArticles([]);

            } finally {

                if (!cancelled) {
                    setLoading(false);
                }

            }
        }

        loadArticles();

        return () => {
            cancelled = true;
        };

    }, [
        page,
        categoryId,
        search,
        sort,
    ]);

    useEffect(() => {

        let cancelled = false;

        async function loadCategories() {

            try {

                const response =
                    await getArticleCategories();

                if (cancelled) {
                    return;
                }

                setCategories([
                    // {
                    //     id: "all",
                    //     title: "همه مقالات",
                    // },
                    ...(response.data || []),
                ]);

            } catch (error) {

                console.error(
                    "Failed to load article categories:",
                    error
                );

            }

        }

        loadCategories();

        return () => {
            cancelled = true;
        };

    }, []);


    /*
    |--------------------------------------------------------------------------
    | Featured article
    |--------------------------------------------------------------------------
    */

    const featuredArticle = useMemo(() => {

        if (!articles.length) {
            return null;
        }

        return articles[0];

    }, [articles]);


    /*
    |--------------------------------------------------------------------------
    | Regular articles
    |--------------------------------------------------------------------------
    */

    const regularArticles = useMemo(() => {

        if (!articles.length) {
            return [];
        }

        return articles.slice(1);

    }, [articles]);


    /*
    |--------------------------------------------------------------------------
    | Category change
    |--------------------------------------------------------------------------
    */
  function handleCategoryChange(newCategoryId) {
    const params = new URLSearchParams(searchParams);

    // "All" means: remove category filtering
    if (
        !newCategoryId ||
        newCategoryId === "all" ||
        newCategoryId === "0"
    ) {
        params.delete("category");
    } else {
        params.set("category", newCategoryId);
    }

    // Always reset pagination when category changes
    params.delete("page");

    setSearchParams(params);
}

    /*
    |--------------------------------------------------------------------------
    | Pagination
    |--------------------------------------------------------------------------
    */

    function handlePageChange(newPage) {

        if (newPage < 1) {
            return;
        }


        if (
            pagination &&
            newPage > pagination.last_page
        ) {
            return;
        }


        const params =
            new URLSearchParams(
                searchParams
            );


        if (newPage === 1) {

            params.delete("page");

        } else {

            params.set(
                "page",
                newPage
            );
        }


        setSearchParams(params);


        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    }

    function ArticleError({
        message,
        onRetry,
    }) {
        return (
            <div className="articles-state">

                <div className="articles-state__icon">
                    ⚠️
                </div>

                <h3>
                    دریافت مقالات با مشکل مواجه شد
                </h3>

                <p>
                    {message}
                </p>

                <button
                    type="button"
                    onClick={onRetry}
                >
                    تلاش مجدد
                </button>

            </div>
        );
    }


    function handleSearch(value) {

        const params =
            new URLSearchParams(
                searchParams
            );


        if (value.trim()) {

            params.set(
                "search",
                value.trim()
            );

        } else {

            params.delete(
                "search"
            );
        }


        // Search always starts from page 1
        params.delete("page");


        setSearchParams(params);
    }



    function ArticleSort({
        value,
        onChange,
    }) {
        return (
            <select
                className="article-sort"
                value={value}
                onChange={(event) =>
                    onChange(event.target.value)
                }
            >
                <option value="latest">
                    جدیدترین
                </option>

                <option value="popular">
                    پربازدیدترین
                </option>

                <option value="oldest">
                    قدیمی‌ترین
                </option>

                <option value="title">
                    بر اساس عنوان
                </option>
            </select>
        );
    }
    function handleSort(value) {

        const params =
            new URLSearchParams(
                searchParams
            );


        if (value) {

            params.set(
                "sort",
                value
            );

        } else {

            params.delete(
                "sort"
            );
        }


        // Sorting starts from page 1
        params.delete("page");


        setSearchParams(params);
    }

     window.scrollTo({
            top: 0,
            behavior: "smooth",
        });


    return (
        <main className="articles-page">

            <ArticlesHero />

            <HomeContainer>

                <ArticleSearch
                    value={search}
                    onSearch={handleSearch}
                />

                <section className="articles-container">

                    

                    <ArticleCategories
                        categories={categories}
                        selectedCategory={categoryId}
                        onCategoryChange={
                            handleCategoryChange
                        }
                    />


                    {loading && (
                        <ArticleLoading />
                    )}


                    {!loading && error && (
                        <ArticleError
                            message={error}
                            onRetry={() => {
                                setSearchParams(
                                    new URLSearchParams(
                                        searchParams
                                    )
                                );
                            }}
                        />
                    )}


                    {!loading &&
                        !error &&
                        articles.length > 0 && (
                            <>
                                <FeaturedArticle
                                    article={featuredArticle}
                                />
                                <div className="articles-section-header">

                                    <div>

                                        <span className="articles-section-header__eyebrow">
                                            مجله سلامت
                                        </span>

                                        <h2>
                                            جدیدترین مقالات
                                        </h2>

                                    </div>

                                    <div className="articles-section-header__actions">

                                        {pagination && (
                                            <span className="articles-section-header__count">
                                                {pagination.total.toLocaleString("fa-IR")}
                                                {" "}
                                                مقاله
                                            </span>
                                        )}

                                        <ArticleSort
                                            value={sort}
                                            onChange={handleSort}
                                        />

                                    </div>

                                </div>


                                <ArticleGrid
                                    articles={
                                        regularArticles
                                    }
                                />


                                <ArticlePagination
                                    currentPage={
                                        pagination?.current_page || 1
                                    }
                                    lastPage={
                                        pagination?.last_page || 1
                                    }
                                    onPageChange={handlePageChange}
                                />


                          
                            </>
                        )}


                    {!loading &&
                        !error &&
                        articles.length === 0 && (
                            <ArticleEmpty
                                onReset={() => {
                                    setSearchParams(
                                        {}
                                    );
                                }}
                            />
                        )}

                </section>
            </HomeContainer>



        </main>


    );

    function ArticleLoading() {
        return (
            <div className="article-loading">

                <div className="article-loading__featured">
                    <div className="skeleton skeleton-image" />

                    <div className="article-loading__content">
                        <div className="skeleton skeleton-small" />
                        <div className="skeleton skeleton-title" />
                        <div className="skeleton skeleton-text" />
                        <div className="skeleton skeleton-text" />
                    </div>
                </div>


                <div className="article-loading__grid">

                    {Array.from({ length: 6 }).map(
                        (_, index) => (
                            <div
                                className="article-loading__card"
                                key={index}
                            >
                                <div className="skeleton skeleton-card-image" />

                                <div className="article-loading__card-content">
                                    <div className="skeleton skeleton-small" />
                                    <div className="skeleton skeleton-title" />
                                    <div className="skeleton skeleton-text" />
                                </div>
                            </div>
                        )
                    )}

                </div>

            </div>
        );
    }
}